import type { IconName } from '@nexus/ui';
import type { SessionInfo } from '../api/types';
import type { WebModule } from '../modules/define';
import { managedSettingsPages } from '../modules/settings';
import { canIn } from '../session/session';

export type AdminArea = 'company' | 'platform';

export interface AdminSection {
  /** Key below `admin.sections`. */
  id:
    | 'accounts'
    | 'groups'
    | 'roles'
    | 'settings'
    | 'sso'
    | 'notifications'
    | 'integrations'
    | 'customFields'
    | 'shares'
    | 'privacy'
    | 'ai'
    | 'branding'
    | 'audit'
    | 'companies'
    | 'modules'
    | 'companyModules'
    | 'platformAccess'
    | 'platformAudit'
    | 'operations';
  path: string;
  icon: IconName;
  area: AdminArea;
  /**
   * Whether the person may open it, given the modules of the app. The API checks every call
   * again.
   */
  visible: (session: SessionInfo, modules: readonly WebModule[]) => boolean;
}

const can = (permission: string) => (session: SessionInfo) => canIn(session, permission);

/**
 * The page holds the settings of the kernel and links the settings pages of the modules, so it
 * shows up with `company.settings.manage`, for superadmins (settings of all companies) and with
 * one module settings page the person manages.
 */
function canManageSomeSetting(session: SessionInfo, modules: readonly WebModule[]): boolean {
  return (
    session.superadmin ||
    canIn(session, 'company.settings.manage') ||
    managedSettingsPages(
      modules.map((m) => m.manifest),
      session,
    ).length > 0
  );
}

/** Pages of the administration, in the order of the navigation. */
export const ADMIN_SECTIONS: readonly AdminSection[] = [
  {
    id: 'accounts',
    path: '/admin/accounts',
    icon: 'users',
    area: 'company',
    visible: can('company.accounts.read'),
  },
  {
    id: 'groups',
    path: '/admin/groups',
    icon: 'network',
    area: 'company',
    visible: can('company.groups.read'),
  },
  {
    id: 'roles',
    path: '/admin/roles',
    icon: 'key-round',
    area: 'company',
    visible: can('company.roles.read'),
  },
  {
    id: 'companyModules',
    path: '/admin/modules',
    icon: 'blocks',
    area: 'company',
    visible: can('company.modules.manage'),
  },
  {
    id: 'settings',
    path: '/admin/settings',
    icon: 'sliders-horizontal',
    area: 'company',
    visible: canManageSomeSetting,
  },
  {
    id: 'sso',
    path: '/admin/sso',
    icon: 'log-in',
    area: 'company',
    visible: can('company.sso.manage'),
  },
  {
    id: 'notifications',
    path: '/admin/notifications',
    icon: 'bell',
    area: 'company',
    visible: can('company.notifications.manage'),
  },
  {
    id: 'integrations',
    path: '/admin/integrations',
    icon: 'plug',
    area: 'company',
    visible: can('company.integrations.manage'),
  },
  {
    id: 'customFields',
    path: '/admin/custom-fields',
    icon: 'layers',
    area: 'company',
    visible: can('company.custom-fields.manage'),
  },
  {
    id: 'shares',
    path: '/admin/shares',
    icon: 'share-2',
    area: 'company',
    visible: can('company.shares.manage'),
  },
  {
    id: 'privacy',
    path: '/admin/privacy',
    icon: 'shield-check',
    area: 'company',
    visible: can('company.privacy.manage'),
  },
  {
    id: 'ai',
    path: '/admin/ai',
    icon: 'sparkles',
    area: 'company',
    visible: can('company.ai.manage'),
  },
  {
    id: 'branding',
    path: '/admin/branding',
    icon: 'palette',
    area: 'company',
    visible: can('company.branding.manage'),
  },
  {
    id: 'audit',
    path: '/admin/audit',
    icon: 'scroll-text',
    area: 'company',
    visible: can('company.audit.read'),
  },
  {
    id: 'companies',
    path: '/admin/platform/companies',
    icon: 'building-2',
    area: 'platform',
    visible: can('platform.companies.manage'),
  },
  {
    id: 'modules',
    path: '/admin/platform/modules',
    icon: 'blocks',
    area: 'platform',
    visible: can('platform.modules.manage'),
  },
  {
    id: 'platformAccess',
    path: '/admin/platform/access',
    icon: 'shield',
    area: 'platform',
    visible: can('platform.superadmin'),
  },
  {
    id: 'platformAudit',
    path: '/admin/platform/audit',
    icon: 'scroll-text',
    area: 'platform',
    visible: can('platform.audit.read'),
  },
  {
    id: 'operations',
    path: '/admin/platform/operations',
    icon: 'activity',
    area: 'platform',
    visible: can('platform.metrics.read'),
  },
];

export function visibleSections(
  session: SessionInfo,
  modules: readonly WebModule[],
): AdminSection[] {
  return ADMIN_SECTIONS.filter((section) => section.visible(session, modules));
}

export function adminSection(id: AdminSection['id']): AdminSection {
  return ADMIN_SECTIONS.find((section) => section.id === id)!;
}
